/* eslint-disable @typescript-eslint/no-shadow */
import { ClassNames, useTheme } from '@emotion/react';
import {
  Accordion,
  Card,
  CardItemBreed,
  CardItemContainer,
  CardItemWrapper,
  Container,
  EmptyOverlay,
  HeaderSearch,
  Illustration,
  InputLabel,
  InputSelectField,
  InputWrapper,
  LeadForm,
  Section,
  SectionSubtitle,
  SectionTitle,
  VetAdviceBanner,
} from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { Form } from 'formik';
import { useRouter } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import type { FC } from 'react';
import { TbBellRinging } from 'react-icons/tb';
import { useMediaQuery } from 'react-responsive';
import bg from './assets/bg.svg';
import * as Styled from './styled';
import type { BreedComparatorPageProps } from './types';

export * from './types';

export const BreedComparatorPage: FC<BreedComparatorPageProps> = (props) => {
  const {
    breedOptions,
    formattedBreeds,
    formattedCategories,
    showNewsletterForm,
    newsletterForm,
    renderSubscribeBtn,
  } = props;
  const { translate, countryCode } = useRouter();
  const theme = useTheme();

  const hasCategories =
    formattedCategories &&
    formattedCategories.breeders.length > 0 &&
    formattedCategories.litters.length > 0;

  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.phone}px)` });

  const showVetAdviceBanner = [CountryCode.BE, CountryCode.FR, CountryCode.NL].includes(
    countryCode,
  );
  return (
    <>
      <HeaderSearch title={translate('breedComparator.title')} />

      {!!showNewsletterForm && <LeadForm {...newsletterForm} style={{ marginTop: '-15px' }} />}

      {!showNewsletterForm && (
        <Styled.BgContentWrapper>
          {formattedBreeds && <Styled.BgImage src={bg} />}

          <Card size="fullscreen" style={{ height: 'auto' }}>
            <Form>
              <Styled.Row>
                <InputWrapper>
                  <InputLabel required>{translate('common.inputs.breed.label')} #1</InputLabel>
                  <InputSelectField name="selectedBreeds[0]" options={breedOptions} />
                </InputWrapper>

                <InputWrapper>
                  <InputLabel required>{translate('common.inputs.breed.label')} #2</InputLabel>
                  <InputSelectField name="selectedBreeds[1]" options={breedOptions} />
                </InputWrapper>
              </Styled.Row>
            </Form>

            {formattedBreeds ? (
              <>
                <Styled.Row>
                  {formattedBreeds.general.map((breed) => (
                    <Styled.Image key={breed.id} src={breed.img} alt="" />
                  ))}
                </Styled.Row>

                <Styled.Row>
                  {formattedBreeds.general.map((breed) => (
                    <Styled.SubscribeButtonWrapper key={breed.id}>
                      {renderSubscribeBtn(breed.name)}
                    </Styled.SubscribeButtonWrapper>
                  ))}
                </Styled.Row>

                {isMobile && (
                  <Styled.HelperBox>
                    <TbBellRinging />
                    {translate(
                      'common.sections.breedInterestSubscription.subscribeToBreedHelperText',
                    )}
                  </Styled.HelperBox>
                )}

                <ClassNames>
                  {({ css: classNameCss }) => {
                    const labelClassname = classNameCss(Styled.accordionLabelStyle(theme));
                    return (
                      <Accordion
                        labelClass={labelClassname}
                        noScroll
                        tabs={[
                          {
                            title: translate('breedComparator.sections.score'),
                            defaultChecked: true,
                            content: (
                              <Styled.Content>
                                {formattedBreeds.scores.map((node, aIndex) => (
                                  <Styled.Category key={`${aIndex}`}>
                                    <SectionSubtitle>{node.label}</SectionSubtitle>
                                    <div>
                                      {node.nodes.map((child, bIndex) => (
                                        <div key={`${aIndex}-${bIndex}`}>
                                          {child.label && (
                                            <Styled.CategoryTitle>
                                              {child.label}
                                            </Styled.CategoryTitle>
                                          )}
                                          <Styled.Row>
                                            {child.values?.map((value, cIndex) => (
                                              <Styled.Item key={`${aIndex}-${bIndex}-${cIndex}`}>
                                                {value}
                                              </Styled.Item>
                                            ))}
                                          </Styled.Row>
                                        </div>
                                      ))}
                                    </div>
                                  </Styled.Category>
                                ))}
                              </Styled.Content>
                            ),
                          },
                          {
                            title: translate('breedComparator.sections.overview'),
                            content: (
                              <Styled.Content>
                                <Styled.Row>
                                  {formattedBreeds.overview.map((values, index) => {
                                    return <div key={index}>{values}</div>;
                                  })}
                                </Styled.Row>
                              </Styled.Content>
                            ),
                          },
                          {
                            title: translate('breedComparator.sections.historic'),
                            content: (
                              <Styled.Content>
                                <Styled.Row>
                                  {formattedBreeds.historic.map((values, index) => {
                                    return <div key={index}>{values}</div>;
                                  })}
                                </Styled.Row>
                              </Styled.Content>
                            ),
                          },
                          {
                            title: translate('breedComparator.sections.morphology'),
                            content: (
                              <Styled.Content>
                                <Styled.Row>
                                  {formattedBreeds.morphology.map((values, index) => {
                                    return <div key={index}>{values}</div>;
                                  })}
                                </Styled.Row>
                              </Styled.Content>
                            ),
                          },
                          {
                            title: translate('breedComparator.sections.fragilities'),
                            content: (
                              <Styled.Content>
                                <Styled.Row>
                                  {formattedBreeds.fragilities.map((values, index) => {
                                    return <div key={index}>{values}</div>;
                                  })}
                                </Styled.Row>
                              </Styled.Content>
                            ),
                          },
                          {
                            title: translate('breedComparator.sections.advices'),
                            content: (
                              <Styled.Content>
                                <Styled.Row>
                                  {formattedBreeds.advices.map((values, index) => {
                                    return <div key={index}>{values}</div>;
                                  })}
                                </Styled.Row>
                              </Styled.Content>
                            ),
                          },
                        ]}
                      />
                    );
                  }}
                </ClassNames>
                {showVetAdviceBanner ? (
                  <Styled.VetAdviceWrapper>
                    <VetAdviceBanner />
                  </Styled.VetAdviceWrapper>
                ) : null}
              </>
            ) : (
              <EmptyOverlay
                title={translate('breedComparator.empty')}
                icon={<Illustration type="litter" />}
              />
            )}
          </Card>

          {hasCategories && (
            <Container>
              <Section>
                <SectionTitle>{translate('homeLanding.breederCategories.title')}</SectionTitle>

                <CardItemContainer>
                  {formattedCategories?.breeders.map((item, index) => (
                    <CardItemWrapper key={index} skipFullWidthPhone>
                      <CardItemBreed
                        name={item.name}
                        image={item.image}
                        link={item.link}
                        footerText={translate('common.buttons.seeBreedings')}
                        dontTruncate
                        shrinkHeightMobile
                      />
                    </CardItemWrapper>
                  ))}
                </CardItemContainer>
              </Section>

              <Section>
                <SectionTitle>{translate('homeLanding.litterCategories.title')}</SectionTitle>

                <CardItemContainer>
                  {formattedCategories?.litters.map((item, index) => (
                    <CardItemWrapper key={index} skipFullWidthPhone>
                      <CardItemBreed
                        name={item.name}
                        image={item.image}
                        link={item.link}
                        footerText={translate('common.buttons.seeLitters')}
                        dontTruncate
                        shrinkHeightMobile
                      />
                    </CardItemWrapper>
                  ))}
                </CardItemContainer>
              </Section>
            </Container>
          )}
        </Styled.BgContentWrapper>
      )}
    </>
  );
};
